import * as React from 'react'
import { Route, withRouter, Switch } from 'react-router-dom'
import ErrorPage from './pages/ErrorPage'
import RegistrationPage from './pages/RegistrationPage'
import ThankYouPage from './pages/ThankYouPage'
import { registerUser } from './lib/register'
import { getStoryblokData, IStoryblokResponseCollection } from './lib/storyblok'
import { ThemeProvider } from './theme'
import { getTimout, isReCaptchaEnabled } from './lib/config'
import LoadingIcon from './components/Loading';
import Fonts from './components/Fonts';
import PolicyPage from './pages/PolicyPage';
import './styles/TouristPage.styles'
import { ITouristContent } from './pages/TouristPage'
import { Title } from './Title'

export interface ITrackingData {
    country?: string
    city?: string
    postcode?: string
}

interface IAppState {
    loading: boolean
    locale: string
    language: string
    url: string
    welcomeContent: any
    touristContent?: ITouristContent
    thankYouContent: any
    termsAndConditions: any
    privacyPolicy: any
    trackingData: ITrackingData
    theme: any
    hideQRCode?: boolean
}

interface IAppProps {
    history: any
}

export interface IRegistrationSubmission {
    fullname: string;
    email: string;
    locale: string;
    city?: string
    countryCode: string
    postcode?: string
    subscription_opt_in: boolean
    source: string
    hideQRCode?: boolean
    recaptchaValue?: string
}

class App extends React.Component<IAppProps, IAppState> {

    private timeout: number

    constructor(props: any) {
        super(props);

        this.timeout = getTimout()

        this.state = {
            url: '',
            locale: 'en-au',
            language: 'en',
            welcomeContent: null,
            thankYouContent: null,
            termsAndConditions: null,
            privacyPolicy: null,
            trackingData: {},
            theme: {
                colors: null,
                logo: {imageUrl: null, alt: null},
                fonts: null,
                storyblokError: false,
            },
            loading: false
        }

        this.renderRegistration = this.renderRegistration.bind(this);
        this.renderThankYou = this.renderThankYou.bind(this);
        this.renderError = this.renderError.bind(this);
        this.renderTermsConditions = this.renderTermsConditions.bind(this);
        this.renderPrivacyPolicy = this.renderPrivacyPolicy.bind(this);
        this.goToThankYou = this.goToThankYou.bind(this);
        this.returnToRegistrationPage = this.returnToRegistrationPage.bind(this);
        this.getData = this.getData.bind(this);
        this.changeLocale = this.changeLocale.bind(this);

        this.getData(this.state.locale);
    }

    public renderRegistration(): JSX.Element {
        if (!this.state.theme.colors || this.state.loading) {
            return <LoadingIcon />
        }
        return (
            <ThemeProvider theme={this.state.theme}>
                <React.Fragment>
                    <Title />
                    <Fonts fonts={this.state.theme.fonts} />
                    <RegistrationPage
                        changeLocale={this.changeLocale}
                        content={this.state.welcomeContent}
                        touristContent={this.state.touristContent}
                        logo={this.state.theme.logo}
                        locale={this.state.locale}
                        language={this.state.language}
                        submitRegistration={this.submitRegistration}
                    />
                </React.Fragment>
            </ThemeProvider>
        )
    }

    public renderThankYou(): JSX.Element {

        if (!this.state.theme.colors || this.state.loading) {
            return <LoadingIcon />
        }

        return (
            <ThemeProvider theme={this.state.theme}>
                <React.Fragment>
                    <Fonts fonts={this.state.theme.fonts} />
                    <ThankYouPage
                        url={this.state.url}
                        locale={this.state.locale}
                        content={this.state.thankYouContent}
                        hideQRCode={this.state.hideQRCode}
                        logo={this.state.theme.logo}
                        changeLocale={this.changeLocale}
                        trackingData={this.state.trackingData}
                        returnToRegistrationPage={this.returnToRegistrationPage}
                        timeout={this.timeout}
                    />
                </React.Fragment>
            </ThemeProvider>
        )
    }

    public renderError(): JSX.Element {
        if (!this.state.theme.colors) {
            return <LoadingIcon />
        }
        return (
            <ThemeProvider theme={this.state.theme}>
                <React.Fragment>
                    {this.state.theme.storyblokError ? null : <Fonts fonts={this.state.theme.fonts} />}
                    <ErrorPage
                        logo={this.state.theme.logo}
                        returnToRegistrationPage={this.returnToRegistrationPage}
                        storyblokError={this.state.theme.storyblokError}
                    />
                </React.Fragment>
            </ThemeProvider>
        )
    }

    public renderTermsConditions() {
        if (!this.state.theme.colors) {
            return <LoadingIcon />
        }
        return (
            <ThemeProvider theme={this.state.theme}>
                <React.Fragment>
                    <Fonts fonts={this.state.theme.fonts} />
                    <PolicyPage
                        content={this.state.termsAndConditions}
                        logo={this.state.theme.logo}
                        returnToRegistrationPage={this.returnToRegistrationPage}
                    />
                </React.Fragment>
            </ThemeProvider>
        )
    }

    public renderPrivacyPolicy() {
        if (!this.state.theme.colors) {
            return <LoadingIcon />
        }
        return (
            <ThemeProvider theme={this.state.theme}>
                <React.Fragment>
                    <Fonts fonts={this.state.theme.fonts} />
                    <PolicyPage
                        content={this.state.privacyPolicy}
                        logo={this.state.theme.logo}
                        returnToRegistrationPage={this.returnToRegistrationPage}
                    />
                </React.Fragment>
            </ThemeProvider>
        )
    }


    public changeLocale(e: React.FormEvent<HTMLButtonElement>): void {
        e.preventDefault();

        const id = e.currentTarget.id;

        if (id === 'en-au') {
            this.setState({
                locale: id,
                language: id.substring(0, 2),
            })
        } else {
            this.setState({
                locale: id,
                language: id.substring(0, 2),
            })
        }

        this.getData(id);
    }

    public async getData(locale: string): Promise<void> {

        const storyblokResponse: IStoryblokResponseCollection = await getStoryblokData(locale)
        this.setState({
            welcomeContent: storyblokResponse.welcomeContent,
            thankYouContent: storyblokResponse.thankYouContent,
            touristContent: storyblokResponse.touristContent,
            termsAndConditions: storyblokResponse.termsAndConditions,
            privacyPolicy: storyblokResponse.privacyPolicy,
            theme: {
                colors: storyblokResponse.theme.colors,
                logo: storyblokResponse.theme.logo[0],
                fonts: {
                    light: storyblokResponse.theme.fonts.light,
                    regular: storyblokResponse.theme.fonts.regular,
                    medium: storyblokResponse.theme.fonts.medium,
                    bold: storyblokResponse.theme.fonts.bold,
                    tourismBody: storyblokResponse.theme.fonts.tourismBody,
                    tourismCta: storyblokResponse.theme.fonts.tourismCta
                },
                storyblokError: false
            }
        })
    }

    public render() {
        return (
            <Switch>
                <Route path="/" exact={true} render={this.renderRegistration} />
                <Route path="/error" render={this.renderError} />
                <Route path="/thank-you" render={this.renderThankYou} />
                <Route path="/terms-and-conditions" render={this.renderTermsConditions} />
                <Route path="/privacy-policy" render={this.renderPrivacyPolicy} />
            </Switch >
        )
    }

    public returnToRegistrationPage() {
        this.props.history.push(`/`)
        this.setState({
            url: '',
            locale: 'en-au',
            language: 'en',
            loading: false
        })
        this.getData('en-au')
    }

    private goToThankYou() {
        this.props.history.push(`/thank-you`)
    }

    private submitRegistration = (payload: IRegistrationSubmission): void => {
        this.setState({
            loading: true
        })
        
        if (isReCaptchaEnabled() && payload.recaptchaValue === '') {
            this.setState({
                loading: false
            })

            return
        }

        registerUser(payload).then((response) => {
            if (response.type === 'Error') {
                this.props.history.push(`/error`)
            } else {
                this.setState({
                    url: response.url,
                    locale: payload.locale,
                    loading: false,
                    hideQRCode: payload.hideQRCode,
                    trackingData: {
                        country: payload.countryCode,
                        postcode: payload.postcode,
                        city: payload.city
                    }
                }, this.goToThankYou)
            }
        }).catch((error: any) => {
            this.props.history.push(`/error`)
        })
    }
}


export default withRouter(App)



// WEBPACK FOOTER //
// ./src/App.tsx